import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ChevronLeftIcon,
  PencilIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { usersApi } from '../services/api/users';
import { rolesApi } from '../services/api/roles';
import { Card, H1, H2, H3, Text, Badge, Button, Spinner, Alert } from '../components/ui';
import { Input } from '../components/forms';
import { useToast } from '../components/ui';
import { UserUpdate, Role } from '../types/api';
import { formatDate } from '../utils/date';
import { extractApiErrorMessage } from '../utils/apiError';

const editUserSchema = z.object({
  email: z.string().email('Valid email is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  is_active: z.boolean(),
  is_staff: z.boolean(),
});

type EditUserFormData = z.infer<typeof editUserSchema>;

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const _navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersApi.getUser(userId!),
    enabled: !!userId,
  });

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesApi.getRoles,
  });

  const { data: userRoles } = useQuery({
    queryKey: ['user-roles', userId],
    queryFn: () => rolesApi.getUserRoles(userId!),
    enabled: !!userId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    values: user ? {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      is_active: user.is_active,
      is_staff: user.is_staff,
    } : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UserUpdate) => usersApi.updateUser(userId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsEditing(false);
      addToast({ title: 'Success', message: 'User updated successfully', type: 'success' });
    },
    onError: (err: unknown) => {
      addToast({
        title: 'Error',
        message: extractApiErrorMessage(err, 'Failed to update user'),
        type: 'error',
      });
    },
  });

  const assignRoleMutation = useMutation({
    mutationFn: (roleId: string) => rolesApi.assignRole({ user_id: userId!, role_id: roleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles', userId] });
      addToast({ title: 'Success', message: 'Role assigned', type: 'success' });
    },
    onError: () => {
      addToast({ title: 'Error', message: 'Failed to assign role', type: 'error' });
    },
  });

  const revokeRoleMutation = useMutation({
    mutationFn: (userRoleId: string) => rolesApi.revokeRole(userRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles', userId] });
      addToast({ title: 'Success', message: 'Role revoked', type: 'success' });
    },
    onError: () => {
      addToast({ title: 'Error', message: 'Failed to revoke role', type: 'error' });
    },
  });

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <Alert variant="error">
        Failed to load user details. Please try again later.
      </Alert>
    );
  }

  const assignedRoleIds = new Set(userRoles?.filter(ur => ur.is_active).map(ur => ur.role_id) || []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/users" className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
          <ChevronLeftIcon className="w-4 h-4 mr-1" />
          Back to Users
        </Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-bold text-xl">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </span>
            </div>
            <div>
              <H1>{user.first_name} {user.last_name}</H1>
              <Text className="text-gray-500 dark:text-gray-400">{user.email}</Text>
            </div>
          </div>
          {!isEditing && (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <PencilIcon className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <Card className="p-6 lg:col-span-2">
          <H2 className="mb-4">Profile Information</H2>
          {isEditing ? (
            <form onSubmit={handleSubmit((data) => updateMutation.mutate(data))} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  {...register('first_name')}
                  label="First Name"
                  error={errors.first_name?.message}
                />
                <Input
                  {...register('last_name')}
                  label="Last Name"
                  error={errors.last_name?.message}
                />
              </div>
              <Input
                {...register('email')}
                label="Email"
                type="email"
                error={errors.email?.message}
              />
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input {...register('is_active')} type="checkbox" className="w-4 h-4 text-primary-600 rounded" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input {...register('is_staff')} type="checkbox" className="w-4 h-4 text-primary-600 rounded" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Staff</span>
                </label>
              </div>
              <div className="flex space-x-3 pt-2">
                <Button type="submit" variant="primary" isLoading={updateMutation.isPending}>
                  Save Changes
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel} disabled={updateMutation.isPending}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">First Name</Text>
                  <Text className="font-medium">{user.first_name}</Text>
                </div>
                <div>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">Last Name</Text>
                  <Text className="font-medium">{user.last_name}</Text>
                </div>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Username</Text>
                <Text className="font-medium">{user.username}</Text>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Email</Text>
                <Text className="font-medium">{user.email}</Text>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant={user.is_active ? 'success' : 'warning'}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {user.is_staff && <Badge variant="info">Staff</Badge>}
                {user.is_superuser && <Badge variant="primary">Superuser</Badge>}
              </div>
            </div>
          )}
        </Card>

        {/* Account Info */}
        <Card className="p-6">
          <H2 className="mb-4">Account</H2>
          <div className="space-y-3">
            <div>
              <Text className="text-sm text-gray-500 dark:text-gray-400">Member Since</Text>
              <Text className="font-medium">
                {formatDate(user.date_joined)}
              </Text>
            </div>
            <div>
              <Text className="text-sm text-gray-500 dark:text-gray-400">Organization</Text>
              <Text className="font-medium">{user.organization_id || 'N/A'}</Text>
            </div>
          </div>
        </Card>
      </div>

      {/* Roles */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <ShieldCheckIcon className="w-5 h-5 text-primary-600" />
          <H2>Roles</H2>
        </div>

        {userRoles && userRoles.length > 0 ? (
          <div className="space-y-2 mb-4">
            {userRoles.filter(ur => ur.is_active).map((ur) => {
              const role = roles?.find((r: Role) => r.id === ur.role_id);
              return (
                <div key={ur.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div>
                    <Text className="font-medium">{role?.name || ur.role_id}</Text>
                    {role?.description && (
                      <Text className="text-sm text-gray-500 dark:text-gray-400">{role.description}</Text>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => revokeRoleMutation.mutate(ur.id)}
                    isLoading={revokeRoleMutation.isPending}
                  >
                    Revoke
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <Text className="text-gray-500 dark:text-gray-400 mb-4">No roles assigned.</Text>
        )}

        {roles && roles.length > 0 && (
          <div>
            <H3 className="text-sm mb-2">Assign Role</H3>
            <div className="flex flex-wrap gap-2">
              {roles
                .filter((r: Role) => !assignedRoleIds.has(r.id))
                .map((role: Role) => (
                  <Button
                    key={role.id}
                    variant="outline"
                    size="sm"
                    onClick={() => assignRoleMutation.mutate(role.id)}
                    isLoading={assignRoleMutation.isPending}
                  >
                    + {role.name}
                  </Button>
                ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserDetail;
